import React, { useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';

export interface ColumnDef<T> {
  key: string;
  header: string;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  searchable?: boolean; // Enable column-specific search
  searchPlaceholder?: string; // Custom placeholder for search input
  width?: string;
}

export interface ActionButton {
  label: string;
  action: string;
  variant?: 'primary' | 'secondary' | 'danger';
  icon?: React.ReactNode;
}

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  persona?: 'customer' | 'business' | 'admin';
  searchable?: boolean;
  filterable?: boolean;
  pagination?: boolean;
  sortable?: boolean;
  onRowClick?: (row: T) => void;
  actions?: ActionButton[];
  onAction?: (action: string, row: T) => void;
  className?: string;
  emptyMessage?: string;
  loading?: boolean;
  getRowClassName?: (row: T) => string;
}

const DataTable = <T extends Record<string, any>>({
  data = [],
  columns = [],
  persona = 'customer',
  searchable = false,
  filterable = false,
  pagination = false,
  sortable = false,
  onRowClick,
  actions = [],
  onAction,
  className = '',
  emptyMessage = 'No data available',
  loading = false,
  getRowClassName
}: DataTableProps<T>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filter data based on search term and column filters
  const filteredData = useMemo(() => {
    try {
      if (!Array.isArray(data)) return data || [];
      
      let filtered = data;
      
      // Apply global search term if provided
      if (searchTerm) {
        filtered = filtered.filter(row => {
          if (!row || typeof row !== 'object') return false;
          return columns.some(column => {
            try {
              const value = row[column.key];
              if (value && typeof value === 'object') {
                // Handle objects - check nested properties for customer name/phone
                if (column.key === 'tailor' && typeof value === 'object') {
                  const name = value.name || value.customer_name || '';
                  const phone = value.phone || value.customer_phone || '';
                  return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         phone.includes(searchTerm);
                }
                return JSON.stringify(value).toLowerCase().includes(searchTerm.toLowerCase());
              }
              // Also search customer_name and customer_phone fields directly
              const customerName = row.customer_name || '';
              const customerPhone = row.customer_phone || '';
              return (value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())) ||
                     customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                     customerPhone.includes(searchTerm);
            } catch (_error) {
              console.warn('Error filtering data:', error);
              return false;
            }
          });
        });
      }
      
      // Apply column-specific filters
      const hasColumnFilters = Object.values(columnFilters).some(filter => filter && filter.trim() !== '');
      if (hasColumnFilters) {
        filtered = filtered.filter(row => {
          if (!row || typeof row !== 'object') return false;
          
          return columns.every(column => {
            const filterValue = columnFilters[column.key];
            if (!filterValue || filterValue.trim() === '') return true;
            
            try {
              const value = row[column.key];
              
              // Handle nested objects for customer name/phone
              if (column.key === 'tailor' && typeof value === 'object') {
                const name = value.name || value.customer_name || '';
                const phone = value.phone || value.customer_phone || '';
                return name.toLowerCase().includes(filterValue.toLowerCase()) ||
                       phone.includes(filterValue);
              }
              
              // Handle customer_name and customer_phone directly
              if (column.key === 'customer_name' || column.key === 'customer_phone') {
                // Also check if the row has these fields directly
                const directValue = row[column.key];
                if (directValue) {
                  return String(directValue).toLowerCase().includes(filterValue.toLowerCase());
                }
              }
              
              // Handle arrays (like measurements)
              if (Array.isArray(value)) {
                return value.some(item => 
                  String(item).toLowerCase().includes(filterValue.toLowerCase())
                );
              }
              
              // Handle objects
              if (value && typeof value === 'object') {
                return JSON.stringify(value).toLowerCase().includes(filterValue.toLowerCase());
              }
              
              // Handle regular values
              return value && value.toString().toLowerCase().includes(filterValue.toLowerCase());
            } catch (_error) {
              console.warn(`Error filtering column ${column.key}:`, error);
              return true; // Don't filter out if error
            }
          });
        });
      }
      
      return filtered;
    } catch (_error) {
      console.error('Error in filteredData:', error);
      return [];
    }
  }, [data, searchTerm, columnFilters, columns]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortColumn, sortDirection]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSize, pagination]);

  const handleSort = (column: ColumnDef<T>) => {
    if (!sortable || !column.sortable) return;
    
    if (sortColumn === column.key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column.key);
      setSortDirection('asc');
    }
  };

  const handleAction = (action: string, row: T) => {
    if (onAction) {
      onAction(action, row);
    }
  };

  const handleColumnFilterChange = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
    // Reset to first page when filter changes
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(sortedData.length / pageSize);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow ${className}`}>
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow ${className}`}>
      {/* Global Search (optional, if searchable is true but no column-specific search) */}
      {searchable && !columns.some(col => col.searchable) && (
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search all columns..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {/* Header Row */}
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    column.width ? `w-${column.width}` : ''
                  }`}
                >
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => handleSort(column)}
                      className={`flex items-center space-x-1 w-full ${
                        sortable && column.sortable ? 'hover:text-gray-700 cursor-pointer' : ''
                      }`}
                      disabled={!sortable || !column.sortable}
                    >
                      <span>{column.header}</span>
                      {sortable && column.sortable && (
                        <div className="flex flex-col">
                          {sortColumn === column.key ? (
                            sortDirection === 'asc' ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )
                          ) : (
                            <div className="flex flex-col">
                              <ChevronUp className="w-2 h-2 opacity-50" />
                              <ChevronDown className="w-2 h-2 opacity-50" />
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                    {/* Column-specific search input */}
                    {(column.searchable || (filterable && column.filterable)) && (
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                        <input
                          type="text"
                          placeholder={column.searchPlaceholder || `Search ${column.header.toLowerCase()}...`}
                          value={columnFilters[column.key] || ''}
                          onChange={(e) => handleColumnFilterChange(column.key, e.target.value)}
                          className="w-full pl-7 pr-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
              {actions.length > 0 && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length + (actions.length > 0 ? 1 : 0)} className="px-6 py-12 text-center">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">{emptyMessage}</p>
                    {searchTerm && (
                      <p className="text-sm mt-1">Try adjusting your search terms</p>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => {
                const rowClassName = getRowClassName ? getRowClassName(row) : '';
                return (
                <tr
                  key={index}
                  className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''} ${rowClassName}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {column.render ? column.render(row[column.key], row) : (() => {
                        const value = row[column.key];
                        if (value && typeof value === 'object') {
                          // Try to extract meaningful string from object
                          if (value.name) return value.name;
                          if (value.title) return value.title;
                          if (value.label) return value.label;
                          if (value.id) return value.id;
                          return JSON.stringify(value);
                        }
                        return String(value || '');
                      })()}
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {actions.map((action) => (
                          <button
                            key={action.action}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAction(action.action, row);
                            }}
                            className={`px-3 py-1 rounded text-xs font-medium ${
                              action.variant === 'danger'
                                ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                : action.variant === 'primary'
                                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                            }`}
                          >
                            {action.icon && <span className="mr-1">{action.icon}</span>}
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} results
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
